'use client';

import React, { useMemo, useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from '@velocity-exchange/icons';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import { cj } from 'src/utils/classNames';
import {
	AssetFilter,
	EarnProduct,
	EarnAccordionOptions,
	EarnProductsByAsset,
	isEarnProductByAsset,
} from '../../../hooks/useEarnProduct';
import { EarnProductComponent } from './EarnProductComponent';
import { EarnProductByAssetComponent } from './EarnProductByAssetComponent';
import SortCaret, { SortDirection } from '../../Tables/SortCaret';
import { useExternalVaultStats } from 'src/hooks/query/vaults/base/useExternalVaultStats';
import InlineLoadingBar from 'src/components/InlineLoadingBar/InlineLoadingBar';
import { useTargetHeight } from 'src/hooks/useTargetHeight';
import {
	EARN_PAGE_HEAD_SECTION_ID,
	EXPLORE_COMPARE_HEAD_SECTION_ID,
} from '../constants';

const DriftEarnTable = ({
	productsToRender,
	searchInputValue,
}: {
	productsToRender:
		| EarnProductsByAsset[]
		| EarnProduct[]
		| EarnAccordionOptions[];
	searchInputValue: string;
	currentFilter: AssetFilter;
}) => {
	const headers = [
		{ label: 'Asset/Product', sortKey: null },
		{ label: 'Est.APY', sortKey: 'apy' },
		{ label: 'Action', sortKey: null },
	];

	const [showAll, setShowAll] = useState(false);
	const [currentSortKey, setCurrentSortKey] = useState<string | null>(null);
	const [currentSortDirection, setCurrentSortDirection] =
		useState<SortDirection>('neutral');
	const toggleSort = (sortKey: string) => {
		if (currentSortKey === sortKey) {
			setCurrentSortDirection((prev) => {
				switch (prev) {
					case 'neutral':
						return 'descending';
					case 'descending':
						return 'ascending';
					case 'ascending':
						return 'neutral';
					default:
						return 'neutral';
				}
			});
		} else {
			setCurrentSortKey(sortKey);
			setCurrentSortDirection('descending');
		}
	};

	const orderedProducts = useMemo(() => {
		if (currentSortDirection === 'ascending') {
			return [...productsToRender].sort((a, b) => {
				const aApy = a.apyToShow ?? 0;
				const bApy = b.apyToShow ?? 0;
				return aApy - bApy;
			});
		} else if (currentSortDirection === 'descending') {
			return [...productsToRender].sort((a, b) => {
				const aApy = a.apyToShow ?? 0;
				const bApy = b.apyToShow ?? 0;
				return bApy - aApy;
			});
		}
		return productsToRender;
	}, [currentSortDirection, productsToRender]);
	// Check external vault stats loading to prevent flickering when external vault values are added later
	// If external API response is slow, ignore external vaults and render anyway after 3 seconds
	const { data: externalVaultStats } = useExternalVaultStats();
	const hasLoadedExternalVaultsStats =
		Object.keys(externalVaultStats ?? {}).length > 0;

	const [shouldRenderAfterTimeout, setShouldRenderAfterTimeout] =
		useState(false);

	useEffect(() => {
		// If already loaded, render immediately and skip timers
		if (hasLoadedExternalVaultsStats) {
			return;
		}
		// Short debounce to mask brief flashes without blanking the table
		const timer = setTimeout(() => setShouldRenderAfterTimeout(true), 3000);
		return () => clearTimeout(timer);
	}, [hasLoadedExternalVaultsStats]);

	const displayedProducts =
		!showAll && !searchInputValue
			? orderedProducts.slice(0, 12)
			: orderedProducts;

	const toggleShowAll = () => setShowAll(!showAll);

	const shouldShowLoading =
		!hasLoadedExternalVaultsStats && !shouldRenderAfterTimeout;

	const topHeaderHeight = useTargetHeight({
		elementId: EARN_PAGE_HEAD_SECTION_ID,
	});
	const filterSectionHeight = useTargetHeight({
		elementId: EXPLORE_COMPARE_HEAD_SECTION_ID,
	});
	const totalHeaderHeight = topHeaderHeight + filterSectionHeight;

	return (
		<>
			<div className="relative col-span-12 flex flex-col gap-y-xs sm:grid sm:grid-cols-[476fr_210fr_594fr] sm:gap-y-0">
				<div
					className="sticky z-40 col-span-3 hidden bg-main-bg sm:grid sm:grid-cols-subgrid"
					style={{ top: `${totalHeaderHeight}px` }}
				>
					<div
						className={cj(
							'px-2xl py-sm',
							'col-span-3',
							'typo-body-xs text-text-secondary',
							'grid grid-cols-subgrid',
							'border-b border-stroke-divider',
							'rounded-tl-sm rounded-tr-sm',
							'bg-container-bg'
						)}
					>
						{headers.map(({ label, sortKey }) => (
							<div
								key={`header_${label}`.replace(/ /g, '')}
								className={cj(
									label === 'Est.APY' && 'text-right',
									label === 'Action' && 'text-right',
									sortKey &&
										'flex cursor-pointer items-center justify-end gap-xs'
								)}
								onClick={sortKey ? () => toggleSort(sortKey) : undefined}
							>
								{label}
								{sortKey && (
									<SortCaret
										state={
											currentSortKey === sortKey
												? currentSortDirection
												: 'neutral'
										}
									/>
								)}
							</div>
						))}
					</div>
				</div>

				{shouldShowLoading ? (
					<div className="col-span-12 flex h-50 w-full items-center justify-center sm:h-100 sm:rounded-b-sm sm:bg-container-bg">
						<InlineLoadingBar />
					</div>
				) : (
					<>
						{displayedProducts.length > 0 ? (
							displayedProducts.map((info, i) => {
								if (!isEarnProductByAsset(info)) {
									return (
										<EarnProductComponent
											key={`single_product_${info.productType}_${info.marketIndex}`}
											earnProduct={info}
											showDetails={true}
											belongsToLastProductRow={
												i === displayedProducts.length - 1
											}
											noWrapper
										/>
									);
								}

								return (
									<EarnProductByAssetComponent
										key={`asset_${info.spotMarket.symbol}`}
										info={info}
										isLastProduct={i === displayedProducts.length - 1}
									/>
								);
							})
						) : (
							<div
								className={cj(
									'col-span-12 flex w-full items-center justify-center bg-container-bg typo-body-sm text-text-disabled',
									'h-50 not-sm:rounded-sm sm:h-100 sm:rounded-b-sm'
								)}
							>
								No Assets Found
							</div>
						)}
					</>
				)}
			</div>
			<div className="col-span-12 flex w-full items-center justify-center">
				{productsToRender.length > 12 && (
					<TextButton
						variant="tertiary"
						size="sm"
						onClick={toggleShowAll}
						rightIcon={showAll ? ChevronUp : ChevronDown}
					>
						{showAll ? 'View Less' : 'View More'}
					</TextButton>
				)}
			</div>
		</>
	);
};

export default DriftEarnTable;
